import { CREDIT_COSTS, CREDIT_PACKS, formatPlanPrice, type CreditPackId } from '@secretario/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { describeError } from '../../lib/api/http.js';
import {
  canBuyCreditsHere,
  ENTITLEMENTS_KEY,
  startCreditPurchase,
  useEntitlements,
} from '../../lib/plan.js';
import { track } from '../../lib/track.js';
import './credits.css';

const PACKS = Object.entries(CREDIT_PACKS) as [CreditPackId, { credits: number; price: number }][];

/**
 * Créditos extras: quando os pedidos do plano acabam, eles seguram a conversa.
 * Também pagam a pesquisa na internet e os minutos ao vivo além do plano.
 * Não expiram. Na web a compra é pela página de pagamento; no app, pela loja.
 */
export function CreditsPanel({
  compact = false,
  surface,
}: {
  compact?: boolean;
  /** De onde a compra saiu (para o acompanhamento). */
  surface: string;
}) {
  const qc = useQueryClient();
  const entitlementsQ = useEntitlements();
  const credits = entitlementsQ.data?.features.credits ?? { balance: 0, webSales: false };
  const [busy, setBusy] = useState<CreditPackId | null>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'wait' | 'bad'; text: string } | null>(
    null,
  );
  const buyable = canBuyCreditsHere(credits.webSales);

  // Volta da página de pagamento (web): o saldo chega por aviso, segundos depois.
  useEffect(() => {
    if (compact) return;
    const value = new URLSearchParams(window.location.search).get('creditos');
    if (value === 'ok') {
      setMessage({ tone: 'wait', text: 'Pagamento recebido. Estou somando os seus créditos…' });
      let tries = 0;
      const timer = setInterval(() => {
        tries += 1;
        void qc.invalidateQueries({ queryKey: ENTITLEMENTS_KEY });
        if (tries >= 8) clearInterval(timer);
      }, 2500);
      setTimeout(() => setMessage({ tone: 'ok', text: 'Créditos somados ao seu saldo.' }), 6000);
      return () => clearInterval(timer);
    }
    if (value === 'cancelado') {
      setMessage({ tone: 'wait', text: 'A compra não foi concluída. Nada foi cobrado.' });
    }
  }, [compact]);

  async function buy(pack: CreditPackId) {
    setBusy(pack);
    setMessage(null);
    track('checkout_started', { kind: 'credits', pack, surface });
    try {
      const done = await startCreditPurchase(pack, credits.balance);
      if (done) {
        await qc.invalidateQueries({ queryKey: ENTITLEMENTS_KEY });
        setMessage({ tone: 'ok', text: `Pronto. ${CREDIT_PACKS[pack].credits} créditos somados.` });
      }
    } catch (error) {
      setMessage({ tone: 'bad', text: describeError(error) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <section
      className={`cr${compact ? ' compact' : ''}`}
      id="creditos"
      aria-label="Créditos extras"
    >
      <div className="cr-hd">
        <div>
          <span className="cr-kicker">Créditos extras</span>
          <h2>{compact ? 'Continuar agora com créditos' : 'Quando o plano acabar, eu continuo'}</h2>
        </div>
        <span className="cr-balance" aria-label={`Saldo: ${credits.balance} créditos`}>
          <b>{credits.balance.toLocaleString('pt-BR')}</b>
          <small>{credits.balance === 1 ? 'crédito' : 'créditos'}</small>
        </span>
      </div>
      {!compact && (
        <ul className="cr-uses">
          <li>
            <b>{CREDIT_COSTS.request}</b> por pedido além do seu plano
          </li>
          <li>
            <b>{CREDIT_COSTS.webSearch}</b> por pesquisa na internet
          </li>
          <li>
            <b>{CREDIT_COSTS.voiceMinute}</b> por minuto de conversa ao vivo além do plano
          </li>
        </ul>
      )}
      {buyable ? (
        <div className="cr-packs">
          {PACKS.map(([id, pack]) => (
            <button
              key={id}
              className="cr-pack"
              onClick={() => void buy(id)}
              disabled={busy !== null}
            >
              <b>{pack.credits.toLocaleString('pt-BR')}</b>
              <small>créditos</small>
              <span className="cr-price">
                {busy === id ? 'Abrindo…' : formatPlanPrice(pack.price)}
              </span>
            </button>
          ))}
        </div>
      ) : // Sem compra ligada neste aparelho: nada de "em breve" (leva 1, P3); o saldo segue acima.
      null}
      {message && (
        <p
          className={`cr-flash ${message.tone}`}
          role={message.tone === 'bad' ? 'alert' : 'status'}
        >
          {message.text}
        </p>
      )}
      {!compact && (
        <p className="cr-note">
          Os créditos não expiram e valem para pedidos, pesquisas e conversa ao vivo. Pagamento
          único, sem assinatura. Créditos não usados podem ser devolvidos em até 7 dias da compra.
        </p>
      )}
    </section>
  );
}
